import React, { useState } from "react";
import { useFirebaseApp } from "../firebase";
import { Alert, AlertType, useModal } from "@surveying-hub-bv/fe-component-library";
import { signInWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword } from "firebase/auth";
import textLogoSVG from "../assets/text-logo.svg";
import { AccoladesBlock } from "../components/ContentElements";
import { TextInput } from "../components/Forms";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Buttons";
import { useTranslation } from "react-i18next";
import { LayoutComponent } from "../structure/layout-components/Layout";

const HeadingBlock = () => {
    // SKYLAR: all the 2xl: settings here are meant to be lg: but the sidebar got in the way and needed the breaks sooner :)
    // Skipping this translation now because we are not using this page for Dutch rebranded apps yet.

    return (
        <div className="flex flex-col max-w-[500px] mx-auto 2xl:max-w-[initial] 2xl:mx-0">
            <h1>
                <img src={textLogoSVG} alt="RTK FIX Login" />
                <span className="sr-only">RTK FIX Login or Register page.</span>
            </h1>
            <h3 className="text-[18px] mt-4">Merge, connect and manage any RTK Network in one centralised platform.</h3>
        </div>
    )
}

/* Sign Up Form */
export const SignUpForm = ({ logInLink, authSwitch }: { logInLink: string, authSwitch?: () => void }) => {
    const auth = useFirebaseApp()
    const navigate = useNavigate()
    const { hideModal } = useModal()
    const { t } = useTranslation()

    const [infoMessage, setInfoMessage] = useState<{ text: string, type: AlertType } | undefined>()
    const [email, setEmail] = useState("")
    const [username, setUsername] = useState("")
    const [password, setPassword] = useState("")
    const [passwordValidation, setPasswordValidation] = useState("")
    const [agreements, setAgreements] = useState(false);

    const formFilled = () => {
        if (email !== "" &&
            username !== "" &&
            password !== "" &&
            passwordValidation !== "" &&
            agreements) {
            return true
        } else {
            return false
        }
    }

    const registerWithEmail = async (email: string, password: string) => {
        if (!auth) {
            throw new Error("Authentication object is not initialized.");
        }
        return createUserWithEmailAndPassword(auth, email, password)
    }

    const registerUser: () => void = async () => {
        const emailRegex =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (email === "" || !emailRegex.test(email)) {
            setInfoMessage({
                type: "error",
                text: t("auth.register.messaging.invalidEmail"),
            });
            return;
        }

        if (username.length < 3) {
            setInfoMessage({
                type: "error",
                text: t("auth.register.messaging.shortUsername"),
            });
            return;
        }

        if (password.length < 8) {
            setInfoMessage({
                type: "error",
                text: t("auth.register.messaging.shortPassword"),
            });
            return;
        }

        if (password !== passwordValidation) {
            setInfoMessage({
                type: "error",
                text: t("auth.register.messaging.passwordMismatch"),
            });
            return;
        }

        setInfoMessage({
            type: "info",
            text: t("auth.register.messaging.registerLoading"),
        });

        try {
            await registerWithEmail(email, password);
            hideModal();
        } catch (error: any) {
            setInfoMessage({
                type: "error",
                text: error.message,
            });
        }
    };

    const authenticateWithGoogle = async () => {
        if (!auth) {
            throw new Error("Authentication object is not initialized.");
        }
        const provider = new GoogleAuthProvider()
        return signInWithPopup(auth, provider)
    }
    return (<>

        <p className="text-[18px] font-bold text-center mb-4">{t("auth.register.title")}</p>


        {infoMessage && <div className="mb-3"><Alert type={infoMessage.type}>{infoMessage.text}</Alert></div>}


        <form onSubmit={(e) => { e.preventDefault(); registerUser(); }}>

            {/* TODO: add Google register here */}
            <button onClick={(e) => { e.preventDefault(); authenticateWithGoogle(); hideModal() }} className="border text-[18px] dark:text-white w-full rounded-full font-bold justify-center items-center gap-x-4 py-3 px-10 flex mx-auto">
                <div className="w-[32px]">
                    <img src="data:image/png;base64,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" alt="logo" width="38px" />
                </div>
                Google
            </button>
            <div className="flex items-center mt-4 mb-0 md:my-6">
                <hr className="flex-grow border-t dark:border-darkMode-layer2border" />
                <p className="font-bold text-center mx-4 uppercase">{t("auth.register.or")}</p>
                <hr className="flex-grow border-t dark:border-darkMode-layer2border" />
            </div>

            <div className="flex flex-col gap-y-5 mt-4 mb-8">
                <TextInput
                    label={t("auth.register.username.label")}
                    type="text"
                    value={username}
                    placeholder={t("auth.register.username.placeholder")}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <TextInput
                    label={t("auth.register.email.label")}
                    type="text"
                    value={email}
                    placeholder={t("auth.register.email.placeholder")}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TextInput
                    label={t("auth.register.password.label")}
                    type="password"
                    value={password}
                    placeholder={t("auth.register.password.placeholder")}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <TextInput
                    label={t("auth.register.confirmPassword.label")}
                    type="password"
                    value={passwordValidation}
                    placeholder={t("auth.register.confirmPassword.placeholder")}
                    onChange={(e) => setPasswordValidation(e.target.value)}
                />
            </div>

            {/* TODO: need a checkbox component here. */}
            <p className="mb-4">
                <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer inline mr-2 align-middle"
                    checked={agreements}
                    onChange={(e) => setAgreements(e.target.checked)}
                />
                <label htmlFor="agree" className="align-middle">{t("auth.register.checkboxLabel")} </label>
                <a href="https://rtkfix.com/terms-conditions/" target="_blank" className="dark:text-darkMode-primary underline" rel="noreferrer">{t("auth.register.ts&cs")}</a>.
            </p>

            <Button label={t("auth.register.buttonText")} type="submit" size="large" disabled={!formFilled()} />
        </form>

        <p className="text-[14px] md:text-[16px] dark:text-darkMode-mutedText text-center">{t("auth.register.toLogin")} {authSwitch !== undefined ? <button onClick={authSwitch} className="dark:text-darkMode-primary underline">{t("auth.logIn")}</button> : <button onClick={() => navigate(logInLink)} className="dark:text-darkMode-primary underline">{t("auth.logIn")}</button>}</p>

        {/*                            <div className="mt-3.5 md:mt-4">
                                <Button
                                    label="Sign in with Google"
                                    icon={{
                                        src: "data:image/png;base64,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",
                                        width: "38px"
                                        }}
                                    onClick={loginWithGoogle}/>
                            </div>
                            <div className="mt-3">
                                <Button
                                    label="Sign in with Facebook"
                                    icon={{
                                        src: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAAlCAYAAACgc9J8AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAFYSURBVHgB7Za/S8NQEMe/95IUs4koxc1RZxHEH+BWdHIQ0YLgZHByc3Z3cSutQ1xUHFx1cVXEv0Co6CDFUVGHapqcV0f7Xn5AhoL5woXHXe7z7iWXI4QUWlj2B7/dYEMRLxIwwaAyGA4IbQm/iX2JvVASaH61UWEbvixHk+5VccHZ9bonoMs0oK6MlU2v1cYcsu+YeAQpZazMIrWXBWSsbHKr7rif9CzLMjLI1jlL7zQlNRtBUvF+h9uHFJWCRJicfdgIAo6uj71dQ54mQYVDMCq8MkViW0O7Ef82aj6wOPUvjOaqjQONf1ysokuQt3khzdns8RPuu62xgyy7A0s6v2I+y+2YMpaa+T0zjh5zhPFTbrBAUcsW4mtPhMiV64A2i/CBiDt/3bcn3oN2BM1Ua5sKlq+LRRyu3Jxun+ti/+RzKmAFrID1J0z742LJCGawdp5RaLdg0A8xjFxdOQBTcwAAAABJRU5ErkJggg==",
                                        width: "19px"
                                        }}/>
                            </div>*/}

    </>
    )
};


/* Login Form */
export const LoginForm = ({ signUpLink, authSwitch }: { signUpLink: string, authSwitch?: () => void }) => {
    const auth = useFirebaseApp()
    const navigate = useNavigate()
    const { hideModal } = useModal()
    const { t } = useTranslation()

    const [infoMessage, setInfoMessage] = useState<{ text: string, type: AlertType } | undefined>()
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")

    const formFilled = () => {
        if (email !== "" && password !== "") {
            return true
        } else {
            return false
        }
    }

    const signInWithEmail = async (email: string, password: string) => {
        if (!auth) {
            throw new Error("Authentication object is not initialized.");
        }
        return signInWithEmailAndPassword(auth, email, password)
    }

    const handleForgotPassword = async () => {
        if (!email) {
            setInfoMessage({ text: t("auth.forgotPassword.enterEmail"), type: 'error' })
            return
        }
        try {
            await sendPasswordResetEmail(auth, email)
            setInfoMessage({ text: t("auth.forgotPassword.confirm"), type: 'info' })
        } catch (error: any) {
            setInfoMessage({ text: error.message, type: 'error' })
        }
    };

    const authenticateWithGoogle = async () => {
        if (!auth) {
            throw new Error("Authentication object is not initialized.");
        }
        const provider = new GoogleAuthProvider()
        return signInWithPopup(auth, provider)
    }

    const loginUser: () => void = async () => {


        const emailRegex =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (email === "" || !emailRegex.test(email)) {
            setInfoMessage({
                type: "error",
                text: t("auth.login.messaging.invalidEmail"),
            });
            return;
        }

        if (password.length < 8) {
            setInfoMessage({
                type: "error",
                text: t("auth.login.messaging.invalidPassword"),
            });
            return;
        }
        setInfoMessage({
            type: "info",
            text: t("auth.login.messaging.loginLoading"),
        });

        try {
            await signInWithEmail(email, password);
            hideModal();
        } catch (error: any) {
            setInfoMessage({
                type: "error",
                text: error.message,
            });
        }


    };

    return <>
        <p className="text-[18px] font-bold text-center mb-4">{t("auth.login.title")}</p>

        {infoMessage && <div className="mb-3"><Alert type={infoMessage.type}>{infoMessage.text}</Alert></div>}

        <form onSubmit={(e) => { e.preventDefault(); loginUser() }}>
            <button onClick={(e) => { e.preventDefault(); authenticateWithGoogle(); hideModal() }} className="flex border-2 dark:border-white dark:hover:border-darkMode-primary transition ease-in-out duration-200 text-[18px] dark:text-white w-full rounded-full font-bold justify-center items-center gap-x-4 py-3 px-10 mx-auto">
                <div className="w-[32px]">
                    <img src="data:image/png;base64,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" alt="logo" width="38px" />
                </div>
                Google
            </button>

            <div className="flex items-center mt-4 mb-0 md:my-6">
                <hr className="flex-grow border-t dark:border-darkMode-layer2border" />
                <p className="font-bold text-center mx-4 uppercase">{t("auth.login.or")}</p>
                <hr className="flex-grow border-t dark:border-darkMode-layer2border" />
            </div>

            <div className="flex flex-col gap-y-5 mt-4">
                <TextInput
                    label={t("auth.login.email.label")}
                    type="text"
                    value={email}
                    placeholder={t("auth.login.email.placeholder")}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TextInput
                    label={t("auth.login.password.label")}
                    type="password"
                    value={password}
                    placeholder={t("auth.login.password.placeholder")}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <button className="text-[14px] dark:text-darkMode-muted hover:underline block font-bold text-right" onClick={(e) => { e.preventDefault(); handleForgotPassword() }}>{t("auth.login.forgotPassword")}</button>

                <Button label={t("auth.login.buttonText")} type="submit" size="large" disabled={!formFilled()} />

                <p className="text-[14px] md:text-[16px] dark:text-darkMode-mutedText text-center">{t("auth.login.toRegister")} {authSwitch !== undefined ? <button onClick={authSwitch} className="dark:text-darkMode-primary underline">{t("auth.signUp")}</button> : <button onClick={() => navigate(signUpLink)} className="dark:text-darkMode-primary underline">{t("auth.signUp")}</button>}</p>
            </div>
        </form>
    </>
}

export const AuthForm = ({ authSwapLink, authType }: { authSwapLink: string, authType: string }) => {
    return (
        <div className="rounded-[24px] dark:bg-darkMode-layer1color border dark:border-darkMode-layer1border px-6 py-8 h-fit flex-grow w-full max-w-[500px] mx-auto">
            {authType === "signup" ? <SignUpForm logInLink={authSwapLink} /> : <LoginForm signUpLink={authSwapLink} />}
        </div>
    )
}

export const LoginPage: React.FC<{ Component: LayoutComponent }> = ({ Component }) => <Component main={{
    content: <LoginRegisterComponent authSwapLink="/signup" authType="login" />,

}} />

export const RegisterPage: React.FC<{ Component: LayoutComponent }> = ({ Component }) => <Component main={{
    content: <LoginRegisterComponent authSwapLink="/login" authType="signup" />,
}} />

const LoginRegisterComponent = ({ authSwapLink, authType }: { authSwapLink: string, authType: string }) => {

    // SKYLAR: all the 2xl: settings here are meant to be lg: but the sidebar got in the way and needed the breaks sooner :)

    return (
        <div className="flex items-center justify-center pb-10">
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-x-2 gap-y-10">

                <div className="2xl:col-span-1 order-1 2xl:order-none 2xl:mb-8">
                    <HeadingBlock />
                </div>

                <div className="2xl:col-span-1 2xl:row-span-2 order-2 2xl:order-none">
                    <AuthForm authSwapLink={authSwapLink} authType={authType} />
                </div>

                <div className="2xl:col-span-1 order-3 2xl:order-none">
                    <AccoladesBlock />
                </div>
            </div>
        </div>
    )
}

